import { ExcelUtils } from '@/helpers/excel-utils.js';

/**
 * HotelBeds Export Service
 * Specific service for exporting HotelBeds activities data
 */
export class HotelBedsExportService {
    /**
     * Export HotelBeds activities to Excel
     * @param {Array} activities - Array of activities from API response
     * @param {string} filename - Name of the file to download
     */
    static exportActivitiesToExcel(activities, filename = 'hotelbeds-activities') {
        try {
            // Transform activities data to flat structure for Excel
            const excelData = this.transformActivitiesData(activities);
            
            // Define headers for HotelBeds activities
            const headers = [
                'Código Actividad',
                'Nombre Actividad', 
                'Código Modalidad',
                'Nombre Modalidad',
                'Rango de Edades',
                'Precio Hotel beds',
                'Precio Taquilla',
                'Precio Obligatorio'
            ];
            
            // Define column widths for HotelBeds activities
            const columnWidths = [
                { wch: 15 }, // Código Actividad
                { wch: 40 }, // Nombre Actividad
                { wch: 20 }, // Código Modalidad
                { wch: 35 }, // Nombre Modalidad
                { wch: 12 }, // Rango de Edades
                { wch: 12 }, // Precio
                { wch: 12 }, // Precio Taquilla
                { wch: 15 }  // Precio Obligatorio
            ];
            
            // Generate filename with timestamp
            const timestamp = ExcelUtils.generateTimestamp();
            const finalFilename = `${filename}-${timestamp}`;
            
            // Use the generic Excel utility
            ExcelUtils.exportToExcel(
                excelData,
                headers,
                columnWidths,
                'Actividades',
                finalFilename
            );
            
        } catch (error) {
            console.error('Error exporting HotelBeds activities to Excel:', error);
            throw new Error('Error al generar el archivo Excel de actividades');
        }
    }
    
    /**
     * Transform HotelBeds activities data to flat structure for Excel
     * @param {Array} activities - Array of activities
     * @returns {Array} Flattened data structure
     */
    static transformActivitiesData(activities) {
        const excelData = [];
        
        activities.forEach(activity => {
            const activityCode = activity.activityCode || activity.code;
            const activityName = activity.content?.name || activity.name || 'Sin nombre';
            
            // Process each modality
            if (activity.modalities && activity.modalities.length > 0) {
                activity.modalities.forEach(modality => {
                    const modalityCode = modality.code;
                    const modalityName = modality.name;
                    
                    // Process each price amount
                    if (modality.amountsFrom && modality.amountsFrom.length > 0) {
                        modality.amountsFrom.forEach(amount => {
                            const ageRange = ExcelUtils.formatAgeRange(amount.ageFrom, amount.ageTo);
                            const price = ExcelUtils.formatPrice(amount.amount);
                            const boxOfficePrice = ExcelUtils.formatPrice(amount.boxOfficeAmount);
                            const mandatoryText = ExcelUtils.formatBoolean(amount.mandatoryApplyAmount);
                            
                            excelData.push({
                                'Código Actividad': activityCode,
                                'Nombre Actividad': activityName,
                                'Código Modalidad': modalityCode,
                                'Nombre Modalidad': modalityName,
                                'Rango de Edades': ageRange,
                                'Precio Hotel beds': price,
                                'Precio Taquilla': boxOfficePrice,
                                'Precio Obligatorio': mandatoryText
                            });
                        });
                    } else {
                        // If no amounts, still add the modality
                        excelData.push({
                            'Código Actividad': activityCode,
                            'Nombre Actividad': activityName,
                            'Código Modalidad': modalityCode,
                            'Nombre Modalidad': modalityName,
                            'Rango de Edades': 'N/A',
                            'Precio Hotel beds': 'N/A',
                            'Precio Taquilla': 'N/A',
                            'Precio Obligatorio': 'N/A'
                        });
                    }
                });
            } else {
                // If no modalities, add basic activity info
                excelData.push({
                    'Código Actividad': activityCode,
                    'Nombre Actividad': activityName,
                    'Código Modalidad': 'N/A',
                    'Nombre Modalidad': 'N/A',
                    'Rango de Edades': 'N/A',
                    'Precio Hotel beds': 'N/A',
                    'Precio Taquilla': 'N/A',
                    'Precio Obligatorio': 'N/A'
                });
            }
        });
        
        return excelData;
    }
} 